import { useState } from 'react';
import { Check, Copy, FileDown, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { QRCodeCanvas } from '@/components/ui/qrcode';
import { generateLeaflet } from '@/lib/leaflet';
import { useToast } from '@/hooks/useToast';

interface AlbumSharePanelProps {
  link: string;
  name: string;
  albumId: string;
  coverUrl?: string;
  hue: number;
}

/** Share UI: secret link, QR code and printable A5 leaflet PDFs. */
export function AlbumSharePanel({ link, name, albumId, coverUrl, hue }: AlbumSharePanelProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [pdfBusy, setPdfBusy] = useState<'color' | 'plain' | null>(null);

  async function copy() {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function pdf(style: 'color' | 'plain') {
    setPdfBusy(style);
    try {
      await generateLeaflet({ link, albumName: name, albumId, coverUrl, hue, style });
    } catch (e) {
      console.error(e);
      toast({
        title: 'PDF generation failed',
        description: 'The leaflet could not be generated. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setPdfBusy(null);
    }
  }

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <p className="text-sm font-medium">Secret link</p>
        <div className="flex gap-2">
          <InputLike value={link} />
          <Button variant="outline" size="icon" onClick={copy} aria-label="Copy link">
            {copied ? <Check className="size-4 text-green-600" /> : <Copy className="size-4" />}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          The key is in the part after <code className="font-mono">#</code> — it is never sent to any server.
          Anyone with this link can view the album.
        </p>
      </div>

      <div className="flex justify-center rounded-xl border bg-white p-4">
        <QRCodeCanvas value={link} size={200} />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">Printable A5 leaflet (4 pages)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Button variant="default" onClick={() => pdf('color')} disabled={pdfBusy !== null}>
            {pdfBusy === 'color' ? <Loader2 className="size-4 animate-spin" /> : <FileDown className="size-4" />}
            Color leaflet
          </Button>
          <Button variant="outline" onClick={() => pdf('plain')} disabled={pdfBusy !== null}>
            {pdfBusy === 'plain' ? <Loader2 className="size-4 animate-spin" /> : <FileDown className="size-4" />}
            Black &amp; white leaflet
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          The color version picks its color from the album ID and includes the cover image; the plain version
          is black text on white. The QR code is on page 3.
        </p>
      </div>
    </div>
  );
}

function InputLike({ value }: { value: string }) {
  return (
    <input
      readOnly
      value={value}
      onFocus={(e) => e.currentTarget.select()}
      className="flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-xs font-mono shadow-xs outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
    />
  );
}
